import type {
  LiquidClass,
  ConfigStep,
  LiquidClassSpec,
} from "@/automation/system-config/systemConfigParser";

export interface LiquidClassSettings {
  aspirate: {
    speed: number;
    zOffset: number;
    zDriveSpeed: number;
    premixCount: number;
    premixSpeed: number;
    waitTime: number;
  };
  dispense: {
    speed: number;
    zOffset: number;
    blowoutVolume: number;
    touchOff: boolean;
    fromBottom: boolean;
    waitAfterDispense: number;
  };
}

const SPEC_MAPPING: Record<
  string,
  { path: string; type: "number" | "boolean" }
> = {
  aspirate_speed: { path: "aspirate.speed", type: "number" },
  aspirate_z_offset: { path: "aspirate.zOffset", type: "number" },
  z_drive_speed: { path: "aspirate.zDriveSpeed", type: "number" },
  premix_count: { path: "aspirate.premixCount", type: "number" },
  premix_speed: { path: "aspirate.premixSpeed", type: "number" },
  wait_time: { path: "aspirate.waitTime", type: "number" },
  dispense_speed: { path: "dispense.speed", type: "number" },
  dest_z_offset: { path: "dispense.zOffset", type: "number" },
  blowout_volume: { path: "dispense.blowoutVolume", type: "number" },
  touch_off: { path: "dispense.touchOff", type: "boolean" },
  dispense_from_bottom: { path: "dispense.fromBottom", type: "boolean" },
  wait_after_dispense: { path: "dispense.waitAfterDispense", type: "number" },
};

/**
 * Retrieves the default liquid class for a given step from the system configuration.
 */
export function getDefaultLiquidClass(
  configStep: ConfigStep,
  liquidClasses: LiquidClass[]
): LiquidClass {
  if (!configStep.requireLiquidClass) {
    throw new Error(`Step "${configStep.id}" does not require a liquid class.`);
  }

  const liquidClassParam = configStep.params.find(
    (p) => p.id === "liquid_class"
  );
  if (!liquidClassParam || !liquidClassParam.default) {
    throw new Error(
      `No default liquid class configured for step "${configStep.id}".`
    );
  }

  const defaultLiquidClass = liquidClasses.find(
    (lc) => lc.id === liquidClassParam.default
  );
  if (!defaultLiquidClass) {
    throw new Error(
      `Default liquid class "${liquidClassParam.default}" not found for step "${configStep.id}".`
    );
  }

  return defaultLiquidClass;
}

/**
 * Compares an override value against the liquid class default.
 * Treats `currentValue === undefined` as "no override" (returns false).
 */
export function valuesDiffer(currentValue: any, defaultValue: any): boolean {
  if (currentValue === undefined) return false;
  if (defaultValue === undefined) return true;
  if (typeof currentValue === "boolean" || typeof defaultValue === "boolean") {
    return Boolean(currentValue) !== Boolean(defaultValue);
  }
  if (typeof currentValue === "number" || typeof defaultValue === "number") {
    return Number(currentValue) !== Number(defaultValue);
  }
  return String(currentValue) !== String(defaultValue);
}

/**
 * Returns true when any aspirate/dispense field on the step has been
 * overridden away from the liquid class's default.
 *
 * Iterates the keys defined on the parsed liquid class (not on the override
 * object) so stale keys from a different LC schema do not produce false
 * positives. Mirrors the per-key semantics used by `useStepSettings.isOverridden`.
 */
export function hasLiquidClassOverrides(
  stepSettings:
    | {
        aspirate?: Record<string, any>;
        dispense?: Record<string, any>;
      }
    | undefined,
  liquidClass: LiquidClass
): boolean {
  if (!stepSettings) return false;
  const defaults = getLiquidClassSettings(liquidClass);
  for (const group of ["aspirate", "dispense"] as const) {
    const overrides = stepSettings[group];
    if (!overrides) continue;
    const groupDefaults = defaults[group] as Record<string, any>;
    for (const key of Object.keys(groupDefaults)) {
      const overrideValue = overrides[key];
      if (overrideValue === undefined) continue;
      if (valuesDiffer(overrideValue, groupDefaults[key])) return true;
    }
  }
  return false;
}

/**
 * Parses all specifications from a liquid class into a structured settings object.
 */
export function getLiquidClassSettings(
  liquidClass: LiquidClass
): LiquidClassSettings {
  const specs = new Map(
    liquidClass.specifications.map((s) => [s.name, s.value])
  );
  const settings = {} as LiquidClassSettings;

  for (const specName in SPEC_MAPPING) {
    if (!specs.has(specName)) {
      throw new Error(
        `Required specification "${specName}" not found in liquid class "${liquidClass.id}".`
      );
    }

    const rawValue = specs.get(specName)!;
    const { path, type } = SPEC_MAPPING[specName];
    const [group, property] = path.split(".");

    let value: number | boolean;
    if (type === "number") {
      value = parseFloat(rawValue as string);
      if (isNaN(value)) {
        throw new Error(
          `Specification "${specName}" has non-numeric value "${rawValue}".`
        );
      }
    } else {
      if (typeof rawValue === "boolean") {
        value = rawValue;
      } else if (typeof rawValue === "string") {
        const lower = rawValue.toLowerCase();
        if (lower === "true") value = true;
        else if (lower === "false") value = false;
        else
          throw new Error(
            `Specification "${specName}" has non-boolean value "${rawValue}".`
          );
      } else {
        throw new Error(
          `Specification "${specName}" has invalid value "${rawValue}".`
        );
      }
    }

    // Dynamically build the settings object
    if (!settings[group as keyof LiquidClassSettings]) {
      (settings as any)[group] = {};
    }
    (settings as any)[group][property] = value;
  }

  return settings;
}
